// Simple toast notification utility
// For a production app, you'd typically use react-hot-toast or similar

interface ToastNotification {
  id: string;
  message: string;
  type: "success" | "error" | "warning" | "info";
  duration?: number;
}

let notifications: ToastNotification[] = [];
const listeners: ((notifications: ToastNotification[]) => void)[] = [];

function notify(listeners: ((notifications: ToastNotification[]) => void)[]) {
  listeners.forEach((listener) => listener(notifications));
}

function addNotification(notification: Omit<ToastNotification, "id">) {
  const id = Math.random().toString(36).substr(2, 9);
  const newNotification = { ...notification, id };

  notifications = [...notifications, newNotification];
  notify(listeners);

  // Auto remove after duration
  setTimeout(() => {
    notifications = notifications.filter((n) => n.id !== id);
    notify(listeners);
  }, notification.duration || 3000);

  return id;
}

export const toast = {
  success: (message: string, duration?: number) =>
    addNotification({
      message,
      type: "success",
      ...(duration && { duration }),
    }),

  error: (message: string, duration?: number) =>
    addNotification({ message, type: "error", ...(duration && { duration }) }),

  warning: (message: string, duration?: number) =>
    addNotification({
      message,
      type: "warning",
      ...(duration && { duration }),
    }),

  info: (message: string, duration?: number) =>
    addNotification({ message, type: "info", ...(duration && { duration }) }),
};

// Hook for React components to listen to toast notifications
export function useToast() {
  return {
    notifications,
    subscribe: (listener: (notifications: ToastNotification[]) => void) => {
      listeners.push(listener);
      return () => {
        const index = listeners.indexOf(listener);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      };
    },
  };
}
